// frontend/src/pages/Migration.jsx
import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Database, Server, Wand2, Rocket, Check, X, Loader2, GitBranch } from 'lucide-react';
import { PipelineGraph } from '../components/wizard/PipelineGraph';
import { useWizardStore } from '../store/useWizardStore';
import { useLanguageStore } from '../store/useLanguageStore';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Suspense, useState, useEffect, useRef } from 'react';

import StepSource from '../components/wizard/StepSource';
import StepTarget from '../components/wizard/StepTarget';
import StepRelationships from '../components/wizard/StepRelationships';
import StepMultiTableSchema from '../components/wizard/StepMultiTableSchema';
import StepMapping from '../components/wizard/StepMapping';
import StepReview from '../components/wizard/StepReview';
import { apiClient } from '../api/client';
import { Modal } from '../components/Modal';
import { LanguageSelector } from '../components/LanguageSelector';

import LogoHorizontal from '../assets/images/datiaFlow_horizontal.jpg';
import Isotipo from '../assets/images/datiaFlow_cuadrado.jpg';

const stepVariants = {
  initial: { opacity: 0, y: 15, scale: 0.98 },
  animate: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.4, ease: [0.16, 1, 0.3, 1] } },
  exit: { opacity: 0, y: -15, scale: 0.98, transition: { duration: 0.2, ease: "easeOut" } }
};

export default function Migration() {
  const {
    currentStep,
    resetWizard,
    loadMigrationData,
    goToStep,
    pipelineStatus,
    migrationName,
    isMultiTable
  } = useWizardStore();

  const wizardMode = useWizardStore((s) => s.getWizardMode());

  const { t } = useLanguageStore();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('id');
  const [isLoading, setIsLoading] = useState(!!editId);
  const [headerStatus, setHeaderStatus] = useState(null);

  const [headerDependsOn, setHeaderDependsOn] = useState([]); // array
  const [headerFailMode, setHeaderFailMode] = useState('normal');
  const [showHeaderDepTip, setShowHeaderDepTip] = useState(false);
  const [availablePipelines, setAvailablePipelines] = useState([]);

  const [modalState, setModalState] = useState({
    isOpen: false, title: '', message: '', showCancel: false, onAccept: null
  });
  const closeModal = () => setModalState(prev => ({ ...prev, isOpen: false }));

  // 馃啎 Funci贸n que retorna pasos din谩micos seg煤n si es N:M o 1:1
  // const getStepsConfig = () => {
  //   const isNtoM = isMultiTable();

  //   if (isNtoM) {
  //     // N:M: 7 pasos
  //     return [
  //       { id: 1, titleKey: 'wizard.steps.source.title', descKey: 'wizard.steps.source.desc', icon: Database },
  //       { id: 2, titleKey: 'wizard.steps.source_relationships.title', descKey: 'wizard.steps.source_relationships.desc', icon: Wand2 },
  //       { id: 3, titleKey: 'wizard.steps.target.title', descKey: 'wizard.steps.target.desc', icon: Server },
  //       { id: 4, titleKey: 'wizard.steps.target_relationships.title', descKey: 'wizard.steps.target_relationships.desc', icon: Wand2 },
  //       { id: 5, titleKey: 'wizard.steps.mapping.title', descKey: 'wizard.steps.mapping.desc', icon: Wand2 },
  //       { id: 6, titleKey: 'wizard.steps.mapping.title', descKey: 'wizard.steps.mapping.desc', icon: Wand2 },
  //       { id: 7, titleKey: 'wizard.steps.review.title', descKey: 'wizard.steps.review.desc', icon: Rocket },
  //     ];
  //   } else {
  //     // 1:1: 4 pasos
  //     return [
  //       { id: 1, titleKey: 'wizard.steps.source.title', descKey: 'wizard.steps.source.desc', icon: Database },
  //       { id: 2, titleKey: 'wizard.steps.target.title', descKey: 'wizard.steps.target.desc', icon: Server },
  //       { id: 3, titleKey: 'wizard.steps.mapping.title', descKey: 'wizard.steps.mapping.desc', icon: Wand2 },
  //       { id: 4, titleKey: 'wizard.steps.review.title', descKey: 'wizard.steps.review.desc', icon: Rocket },
  //     ];
  //   }
  // };


  const getStepsConfig = () => {
    const mode = useWizardStore.getState().getWizardMode();

    // 1:1 → 4 pasos (sin relaciones)
    if (mode === '1:1') {
      return [
        { id: 1, titleKey: 'wizard.steps.source.title', descKey: 'wizard.steps.source.desc', icon: Database, component: 'StepSource' },
        { id: 2, titleKey: 'wizard.steps.target.title', descKey: 'wizard.steps.target.desc', icon: Server, component: 'StepTarget' },
        { id: 3, titleKey: 'wizard.steps.mapping.title', descKey: 'wizard.steps.mapping.desc', icon: Wand2, component: 'StepMapping' },
        { id: 4, titleKey: 'wizard.steps.review.title', descKey: 'wizard.steps.review.desc', icon: Rocket, component: 'StepReview' },
      ];
    }

    // 1:N → 5 pasos (relaciones en destino)
    if (mode === '1:N') {
      return [
        { id: 1, titleKey: 'wizard.steps.source.title', descKey: 'wizard.steps.source.desc', icon: Database, component: 'StepSource' },
        { id: 2, titleKey: 'wizard.steps.target.title', descKey: 'wizard.steps.target.desc', icon: Server, component: 'StepTarget' },
        { id: 3, titleKey: 'wizard.steps.target_relationships.title', descKey: 'wizard.steps.target_relationships.desc', icon: Wand2, component: 'StepRelationships' },
        { id: 4, titleKey: 'wizard.steps.mapping.title', descKey: 'wizard.steps.mapping.desc', icon: Wand2, component: 'StepMapping' },
        { id: 5, titleKey: 'wizard.steps.review.title', descKey: 'wizard.steps.review.desc', icon: Rocket, component: 'StepReview' },
      ];
    }

    // N:1 → 5 pasos (relaciones en origen)
    if (mode === 'N:1') {
      return [
        { id: 1, titleKey: 'wizard.steps.source.title', descKey: 'wizard.steps.source.desc', icon: Database, component: 'StepSource' },
        { id: 2, titleKey: 'wizard.steps.source_relationships.title', descKey: 'wizard.steps.source_relationships.desc', icon: Wand2, component: 'StepRelationships' },
        { id: 3, titleKey: 'wizard.steps.target.title', descKey: 'wizard.steps.target.desc', icon: Server, component: 'StepTarget' },
        { id: 4, titleKey: 'wizard.steps.mapping.title', descKey: 'wizard.steps.mapping.desc', icon: Wand2, component: 'StepMapping' },
        { id: 5, titleKey: 'wizard.steps.review.title', descKey: 'wizard.steps.review.desc', icon: Rocket, component: 'StepReview' },
      ];
    }

    // N:M → 6 pasos (relaciones en ambos lados)
    return [
      { id: 1, titleKey: 'wizard.steps.source.title', descKey: 'wizard.steps.source.desc', icon: Database, component: 'StepSource' },
      { id: 2, titleKey: 'wizard.steps.source_relationships.title', descKey: 'wizard.steps.source_relationships.desc', icon: Wand2, component: 'StepRelationships' },
      { id: 3, titleKey: 'wizard.steps.target.title', descKey: 'wizard.steps.target.desc', icon: Server, component: 'StepTarget' },
      { id: 4, titleKey: 'wizard.steps.target_relationships.title', descKey: 'wizard.steps.target_relationships.desc', icon: Wand2, component: 'StepRelationships' },
      { id: 5, titleKey: 'wizard.steps.mapping.title', descKey: 'wizard.steps.mapping.desc', icon: Wand2, component: 'StepMapping' },
      { id: 6, titleKey: 'wizard.steps.review.title', descKey: 'wizard.steps.review.desc', icon: Rocket, component: 'StepReview' },
    ];
  };

  const STEPS_TO_DISPLAY = getStepsConfig();

  const renderCurrentStep = () => {
    const mode = useWizardStore.getState().getWizardMode();

    // 1:1 → 4 pasos
    if (mode === '1:1') {
      switch (currentStep) {
        case 1: return <StepSource />;
        case 2: return <StepTarget />;
        case 3:
          return (
            <Suspense fallback={<div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-indigo-600" /></div>}>
              <StepMapping />
            </Suspense>
          );
        case 4:
          return (
            <Suspense fallback={<div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-indigo-600" /></div>}>
              <StepReview />
            </Suspense>
          );
        default: return <StepSource />;
      }
    }

    // 1:N → 5 pasos
    if (mode === '1:N') {
      switch (currentStep) {
        case 1: return <StepSource />;
        case 2: return <StepTarget />;
        case 3: return <StepRelationships mode="target" />;
        case 4:
          return (
            <Suspense fallback={<div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-indigo-600" /></div>}>
              <StepMapping />
            </Suspense>
          );
        case 5:
          return (
            <Suspense fallback={<div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-indigo-600" /></div>}>
              <StepReview />
            </Suspense>
          );
        default: return <StepSource />;
      }
    }

    // N:1 → 5 pasos
    if (mode === 'N:1') {
      switch (currentStep) {
        case 1: return <StepSource />;
        case 2: return <StepRelationships mode="source" />;
        case 3: return <StepTarget />;
        case 4:
          return (
            <Suspense fallback={<div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-indigo-600" /></div>}>
              <StepMapping />
            </Suspense>
          );
        case 5:
          return (
            <Suspense fallback={<div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-indigo-600" /></div>}>
              <StepReview />
            </Suspense>
          );
        default: return <StepSource />;
      }
    }

    // N:M → 6 pasos
    switch (currentStep) {
      case 1: return <StepSource />;
      case 2: return <StepRelationships mode="source" />;
      case 3: return <StepTarget />;
      case 4: return <StepRelationships mode="target" />;
      case 5:
        return (
          <Suspense fallback={<div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-indigo-600" /></div>}>
            <StepMapping />
          </Suspense>
        );
      case 6:
        return (
          <Suspense fallback={<div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-indigo-600" /></div>}>
            <StepReview />
          </Suspense>
        );
      default: return <StepSource />;
    }
  };

  const handleExit = () => {
    setModalState({
      isOpen: true,
      title: t('common.alerts.exit_title') || 'Cancelar y Salir',
      message: t('common.alerts.exit_msg') || '¿Estás seguro que deseas salir? Todos los cambios no guardados se perderán.',
      showCancel: true,
      onAccept: () => {
        closeModal();
        resetWizard();
        navigate('/dashboard');
      }
    });
  };

  // ─────────────────────────────────────────────────────────────
  // 🆕 Estado para trackear cambios de modo
  // ─────────────────────────────────────────────────────────────
  const prevModeRef = useRef(null);

  // ─────────────────────────────────────────────────────────────
  // ORIGINAL: Cargar migración al editar desde Dashboard
  // ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!editId) return;
    // Cargar pipelines disponibles para el selector de padre
    apiClient.get('/dashboard/pipelines').then(res => {
      if (res.data.success) {
        const others = res.data.data.filter(p => p.id !== Number(editId));
        setAvailablePipelines(others);
        // Restaurar dependsOn actual
        const current = res.data.data.find(p => p.id === Number(editId));
        if (current?.dependsOn?.length) setHeaderDependsOn(current.dependsOn);
        const depConf = current?.dependsOnConfig || {};
        setHeaderFailMode(depConf.failMode || 'normal');

      }
    }).catch(() => { });
  }, [editId]);

  const saveHeaderDependsOn = async (newIds, failMode = headerFailMode) => {
    if (!editId) return;
    try {
      await apiClient.patch(`/dashboard/pipelines/${editId}/depends-on`, {
        dependsOn: newIds?.length ? newIds : null,
        failMode: failMode,
      });
      setHeaderDependsOn(newIds || []);
    } catch (e) { console.error(e); }
  };

  const toggleHeaderParent = (id) => {
    const next = headerDependsOn.includes(id)
      ? headerDependsOn.filter(x => x !== id)
      : [...headerDependsOn, id];
    saveHeaderDependsOn(next);
  };

  useEffect(() => {
    apiClient.post('/ai-mapping/warmup').catch(() => { });
    if (editId) {
      apiClient.get(`/onboarding/migrations/${editId}`)
        .then(res => {
          if (res.data.success) {
            loadMigrationData(res.data.data);
            setHeaderStatus(res.data.data.status);
            // 🆕 Registrar modo inicial al cargar
            const mode = useWizardStore.getState().getWizardMode();
            prevModeRef.current = mode;
          }
        })
        .catch(err => console.error("Error cargando pipeline:", err))
        .finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
      resetWizard();
      // 🆕 Registrar modo inicial al crear nuevo
      prevModeRef.current = '1:1';
    }
  }, [editId]);

  // ─────────────────────────────────────────────────────────────
  // 🆕 NUEVO: Auto-mapear currentStep cuando cambia el modo
  // ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const mode = useWizardStore.getState().getWizardMode();

    if (!prevModeRef.current || prevModeRef.current === mode) {
      prevModeRef.current = mode;
      return;
    }

    const prevMode = prevModeRef.current;
    prevModeRef.current = mode;

    const stepMapping = {
      '1:1': {
        '1:N': { 1: 1, 2: 2, 3: 3, 4: 4, 5: 5 },     // 1:1 → 1:N (paso 4→5)
        'N:1': { 1: 1, 2: 2, 3: 3, 4: 4, 5: 5 },     // 1:1 → N:1 (paso 4→5)
        'N:M': { 1: 1, 2: 3, 3: 5, 4: 6 },           // 1:1 → N:M (paso 2→3, 3→5, 4→6)
      },
      '1:N': {
        '1:1': { 1: 1, 2: 2, 3: 2, 4: 3, 5: 4 },     // 1:N → 1:1 (paso 3→2, 4→3, 5→4)
        'N:1': { 1: 1, 2: 2, 3: 2, 4: 4, 5: 5 },     // 1:N → N:1 (paso 3→2)
        'N:M': { 1: 1, 2: 3, 3: 4, 4: 5, 5: 6 },     // 1:N → N:M (agrega paso 2)
      },
      'N:1': {
        '1:1': { 1: 1, 2: 2, 3: 3, 4: 3, 5: 4 },     // N:1 → 1:1 (paso 2→2, 3→3, 4→3, 5→4)
        '1:N': { 1: 1, 2: 2, 3: 2, 4: 4, 5: 5 },     // N:1 → 1:N (paso 2→2)
        'N:M': { 1: 1, 2: 2, 3: 3, 4: 4, 5: 5, 6: 6 }, // N:1 → N:M (agrega paso 4)
      },
      'N:M': {
        '1:1': { 1: 1, 2: 2, 3: 2, 4: 2, 5: 3, 6: 4 },  // N:M → 1:1 (elimina pasos 2,4)
        '1:N': { 1: 1, 2: 2, 3: 2, 4: 4, 5: 5, 6: 5 },  // N:M → 1:N (elimina paso 2)
        'N:1': { 1: 1, 2: 2, 3: 3, 4: 2, 5: 4, 6: 5 },  // N:M → N:1 (elimina paso 4)
      },
    };

    const modeMap = stepMapping[prevMode]?.[mode];

    if (modeMap && modeMap[currentStep]) {
      const newStep = modeMap[currentStep];
      if (newStep !== currentStep) {
        console.log(`📍 Modo cambió ${prevMode} → ${mode}. Paso ${currentStep} → ${newStep}`);
        goToStep(newStep);
      }
    }
  }, [useWizardStore((s) => s.getWizardMode()), currentStep, goToStep]);

  const canNavigateToStep = (targetStep) => {
    if (editId) return true;
    return targetStep <= currentStep;
  };

  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-10 h-10 animate-spin text-indigo-600" /></div>;
  }



  return (
    <div className="min-h-screen bg-slate-50 flex flex-col font-sans text-slate-900 selection:bg-indigo-100 selection:text-indigo-900">

      {/* WIZARD TOP HEADER */}
      <header className="bg-white border-b border-slate-200 px-4 md:px-6 py-3 md:py-4 flex items-center justify-between shrink-0 sticky top-0 z-50 shadow-sm">

        {/* LEFT — Logo + nombre + info */}
        <div className="flex items-center gap-2 md:gap-4 min-w-0 flex-1">
          {/* Logo */}
          <div className="flex items-center gap-2 shrink-0">
            <img src={Isotipo} alt="DatiaFlow" className="block md:hidden h-8 w-8 object-cover object-left rounded-md" />
            <img src={LogoHorizontal} alt="DatiaFlow" className="hidden md:block h-10 w-auto" />
          </div>

          <div className="hidden sm:block w-px h-5 bg-slate-300 shrink-0" />

          {/* Nombre + badges — Desktop */}
          <div className="hidden md:flex items-center gap-2 flex-wrap min-w-0">
            <span className="text-sm font-semibold text-slate-900 truncate max-w-[300px]" title={migrationName}>
              {migrationName || t('wizard.new_migration') || 'Nueva Migración'}
            </span>
            {editId && (
              <>
                <span className="bg-slate-100 text-slate-600 font-mono text-[10px] font-bold px-2 py-1 rounded-md border border-slate-200 shadow-sm">
                  ID: {editId}
                </span>
                {Number(headerStatus) === 1 && <span className="bg-emerald-50 text-emerald-700 text-[9px] font-bold uppercase px-1.5 py-0.5 rounded border border-emerald-200 flex items-center"><div className="w-1 h-1 rounded-full bg-emerald-500 mr-1 animate-pulse" />{t('dashboard.status.active') || 'Activo'}</span>}
                {Number(headerStatus) === 2 && <span className="bg-amber-50 text-amber-700 text-[9px] font-bold uppercase px-1.5 py-0.5 rounded border border-amber-200 flex items-center"><div className="w-1 h-1 rounded-full bg-amber-500 mr-1" />{t('dashboard.status.paused') || 'Pausado'}</span>}
                {Number(headerStatus) === 0 && <span className="bg-red-50 text-red-700 text-[9px] font-bold uppercase px-1.5 py-0.5 rounded border border-red-200 flex items-center"><div className="w-1 h-1 rounded-full bg-red-500 mr-1" />{t('dashboard.status.annulled') || 'Anulado'}</span>}
              </>
            )}
            {/* Selector padre Desktop */}
            {editId && (
              <div className="hidden md:flex items-center gap-1.5 ml-2 relative">
                <GitBranch className="w-3.5 h-3.5 text-purple-400 shrink-0" />
                <details className="relative">
                  <summary className="text-[11px] font-medium text-slate-600 border border-slate-200 rounded-lg pl-2 pr-6 py-1 outline-none cursor-pointer bg-white list-none flex items-center justify-between gap-1 max-w-[180px] relative">
                    <span className="truncate">
                      {headerDependsOn.length === 0
                        ? `— ${t('dashboard.actions.independent') || 'Independiente'} —`
                        : headerDependsOn.length === 1
                          ? (() => { const p = availablePipelines.find(x => x.id === headerDependsOn[0]); return p ? `(${p.id}) ${p.name.slice(0, 15)}` : `#${headerDependsOn[0]}`; })()
                          : `${headerDependsOn.length} padres`
                      }
                    </span>
                    <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-400 text-[9px] pointer-events-none">▼</span>
                  </summary>
                  <div className="absolute top-full left-0 mt-1 z-50 bg-white border border-slate-200 rounded-xl shadow-lg min-w-[220px] max-h-52 overflow-y-auto">
                    <div className="p-2 border-b border-slate-100 flex justify-between items-center">
                      <span className="text-[10px] font-bold text-slate-500 uppercase">Espera a:</span>
                      {headerDependsOn.length > 0 && (
                        <button onClick={() => saveHeaderDependsOn([])} className="text-[9px] text-red-500">✕ Limpiar</button>
                      )}
                    </div>
                    {availablePipelines.map(p => (
                      <label key={p.id} className={`flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-purple-50 ${headerDependsOn.includes(p.id) ? 'bg-purple-50' : ''}`}>
                        <input
                          type="checkbox"
                          checked={headerDependsOn.includes(p.id)}
                          onChange={() => toggleHeaderParent(p.id)}
                          className="w-3.5 h-3.5 accent-purple-600"
                        />
                        <span className="text-[11px] text-slate-700 truncate">({p.id}) {p.name}</span>
                      </label>
                    ))}
                    {/* Tip + failMode */}
                    {headerDependsOn.length > 0 && (
                      <div className="border-t border-slate-100 pt-2 mt-1">
                        <button
                          type="button"
                          onClick={() => setShowHeaderDepTip(p => !p)}
                          className="flex items-center gap-1 text-[10px] text-amber-600 w-full px-3 py-1"
                        >
                          <span>💡</span>
                          <span>{t('dashboard.deps.tip_label') || '¿Cómo evalúa el sistema?'}</span>
                          <span className="ml-auto">{showHeaderDepTip ? '▲' : '▼'}</span>
                        </button>
                        {showHeaderDepTip && (
                          <div className="mx-3 mb-2 bg-amber-50 border border-amber-200 rounded p-2 text-[10px] text-amber-800">
                            {headerFailMode === 'strict' && (
                              <p>🔒 {t('dashboard.deps.tip_strict_body') || 'Solo continúa si TODOS los padres terminaron al 100% sin errores.'}</p>
                            )}
                            {headerFailMode === 'normal' && (
                              <ul className="space-y-0.5">
                                <li>✅ {t('dashboard.deps.tip_success') || 'Exitoso → continúa'}</li>
                                <li>✅ {t('dashboard.deps.tip_partial') || 'Algunos datos pasaron → continúa'}</li>
                                <li>⛔ {t('dashboard.deps.tip_partial_empty') || 'Ningún dato pasó → bloquea'}</li>
                                <li>⛔ {t('dashboard.deps.tip_failed') || 'Falló → bloquea'}</li>
                              </ul>
                            )}
                            {headerFailMode === 'permissive' && (
                              <p>⚠️ {t('dashboard.deps.tip_permissive_body') || 'Continúa siempre, aunque fallen las dependencias.'}</p>
                            )}
                          </div>
                        )}
                        {/* Radio buttons failMode */}
                        <div className="px-3 py-1 space-y-1.5">
                          {[
                            { value: 'strict', label: t('dashboard.deps.mode_strict') || 'Estricto (100% sin errores)' },
                            { value: 'normal', label: t('dashboard.deps.mode_normal') || 'Normal (default)' },
                            { value: 'permissive', label: t('dashboard.deps.mode_permissive') || 'Permisivo (siempre continúa)' },
                          ].map(({ value, label }) => (
                            <label key={value} className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="radio"
                                name="headerFailMode"
                                value={value}
                                checked={headerFailMode === value}
                                onChange={() => {
                                  setHeaderFailMode(value);
                                  saveHeaderDependsOn(headerDependsOn, value);
                                }}
                                className="accent-purple-600 w-3.5 h-3.5"
                              />
                              <span className={`text-[11px] ${headerFailMode === value ? 'font-medium text-slate-900' : 'text-slate-600'}`}>
                                {label}
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </details>
              </div>
            )}
          </div>

          {/* Nombre — Mobile: truncado con ID a la derecha */}
          <div className="flex md:hidden flex-col min-w-0 flex-1">

            {/* Fila 1: nombre truncado + ID */}
            <div className="flex items-center gap-1 min-w-0">
              <span className="text-sm font-semibold text-slate-900 truncate" title={migrationName}>
                {migrationName || t('wizard.new_migration') || 'Nueva Migración'}
              </span>
              {editId && (
                <span className="text-[10px] text-slate-400 font-mono shrink-0 ml-1">({editId})</span>
              )}
            </div>

            {/* Fila 2: estado + selector padre (solo en edit mode) */}
            {editId && (
              <div className="flex items-center gap-1.5 mt-0.5 flex-nowrap overflow-x-auto no-scrollbar">
                {Number(headerStatus) === 1 && <span className="bg-emerald-50 text-emerald-700 text-[9px] font-bold uppercase px-1.5 py-0.5 rounded border border-emerald-200 flex items-center shrink-0"><div className="w-1 h-1 rounded-full bg-emerald-500 mr-1 animate-pulse" />{t('dashboard.status.active') || 'Activo'}</span>}
                {Number(headerStatus) === 2 && <span className="bg-amber-50 text-amber-700 text-[9px] font-bold uppercase px-1.5 py-0.5 rounded border border-amber-200 flex items-center shrink-0"><div className="w-1 h-1 rounded-full bg-amber-500 mr-1" />{t('dashboard.status.paused') || 'Pausado'}</span>}
                {Number(headerStatus) === 0 && <span className="bg-red-50 text-red-700 text-[9px] font-bold uppercase px-1.5 py-0.5 rounded border border-red-200 flex items-center shrink-0"><div className="w-1 h-1 rounded-full bg-red-500 mr-1" />{t('dashboard.status.annulled') || 'Anulado'}</span>}
                <GitBranch className="w-3 h-3 text-purple-400 shrink-0" />
                <select
                  value={headerDependsOn || ''}
                  onChange={(e) => saveHeaderDependsOn(e.target.value ? Number(e.target.value) : null)}
                  className="text-[10px] text-slate-600 border border-slate-200 rounded pl-1.5 pr-5 py-0.5 outline-none focus:border-purple-400 bg-white min-w-0 flex-1 max-w-[130px]"
                >
                  <option value="">— {t('dashboard.actions.independent') || 'Indep.'} —</option>
                  {availablePipelines.map(p => {
                    const sl = p.isRunning === 1 ? 'Ejec.' : p.status === 1 ? 'Activo' : p.status === 2 ? 'Pausado' : 'Anulado';
                    return (
                      <option key={p.id} value={p.id}>
                        {p.name.length > 12 ? p.name.slice(0, 12) + '…' : p.name} ({p.id}) {sl}
                      </option>
                    );
                  })}
                </select>
              </div>
            )}
          </div>
        </div>

        {/* RIGHT — Language + Exit (más compacto en mobile) */}
        <div className="flex items-center gap-0.5 md:gap-3 shrink-0 ml-1">
          {/* <div className="hidden md:block"> */}
          <span className="hidden md:block"><LanguageSelector /></span>
          <span className="block md:hidden"><LanguageSelector hideIcon /></span>
          {/* <LanguageSelector /> */}
          {/* </div> */}
          <button
            onClick={handleExit}
            className="text-sm font-bold text-slate-500 hover:text-red-600 flex items-center transition-colors px-2 md:px-3 py-1.5 rounded-lg hover:bg-red-50"
            title={t('common.buttons.exit_no_save') || 'Salir sin guardar'}
          >
            <span className="hidden sm:inline mr-2">{t('common.buttons.cancel_exit') || 'Cancelar y Salir'}</span>
            <X className="w-5 h-5" />
          </button>
        </div>
      </header>

      <div className="flex-1 flex flex-col md:flex-row w-full max-w-[1600px] mx-auto">

        {/* SIDEBAR IZQUIERDO (Desktop) */}
        <aside className="hidden md:flex flex-col w-80 bg-slate-50 border-r border-slate-200 p-8 shrink-0">
          <div className="mb-10">
            <h1 className="text-xl font-semibold tracking-tight">{editId ? (t('wizard.edit_migration') || 'Editar Migración') : (t('wizard.new_migration') || 'Nueva Migración')}</h1>
            <p className="text-sm text-slate-500 mt-1">
              {isMultiTable()
                ? (t('wizard.subtitle_multitable') || 'Configura tu ETL Multi-Tabla')
                : (t('wizard.subtitle') || 'Configura tu ETL en 4 pasos')
              }
            </p>
          </div>

          {/* <nav className="flex-1 relative"> */}
          <nav className="relative">
            <div className="absolute left-5 top-5 bottom-5 w-px bg-slate-200 -z-10" />
            <ul className="space-y-8">
              {STEPS_TO_DISPLAY.map((step) => {
                const isActive = currentStep === step.id;
                const isCompleted = currentStep > step.id;
                const canClick = canNavigateToStep(step.id);
                const Icon = step.icon;

                return (
                  <li key={step.id}
                    onClick={() => canClick && goToStep(step.id)}
                    className={`relative flex items-start group ${canClick ? 'cursor-pointer hover:bg-slate-100 p-2 -ml-2 rounded-xl transition-colors' : 'opacity-60'}`}
                  >
                    <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 bg-slate-50 transition-colors duration-300
                      ${isActive ? 'border-indigo-600 text-indigo-600' : ''}
                      ${isCompleted ? 'border-indigo-600 bg-indigo-600 text-white' : ''}
                      ${!isActive && !isCompleted ? 'border-slate-300 text-slate-400 group-hover:border-slate-400' : ''}
                    `}>
                      {isCompleted ? <Check className="w-5 h-5" strokeWidth={3} /> : <Icon className="w-5 h-5" />}
                    </div>
                    <div className="ml-4 mt-1 flex flex-col">
                      <span className={`text-sm font-medium transition-colors duration-300 ${isActive ? 'text-slate-900' : isCompleted ? 'text-slate-700' : 'text-slate-400'}`}>
                        {t(step.titleKey)}
                      </span>
                      <span className="text-xs text-slate-500 mt-0.5">{t(step.descKey)}</span>
                    </div>
                  </li>
                );
              })}
            </ul>
          </nav>
          {/* 🔧 Minimap del pipeline — se muestra cuando hay tablas configuradas */}
          <PipelineGraph />
        </aside>

        {/* PROGRESS BAR (M贸vil) */}
        <div className="md:hidden bg-white border-b border-slate-200 px-6 py-4 flex flex-col z-10 sticky top-[73px] shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <div className="flex space-x-2 overflow-x-auto pb-1 no-scrollbar w-full">
              {STEPS_TO_DISPLAY.map((step) => {
                const isActive = currentStep === step.id;
                const isCompleted = currentStep > step.id;
                const canClick = canNavigateToStep(step.id);

                return (
                  <button
                    key={step.id}
                    onClick={() => canClick && goToStep(step.id)}
                    disabled={!canClick}
                    className={`flex items-center justify-center shrink-0 px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${isActive ? 'bg-indigo-600 text-white' :
                      isCompleted ? 'bg-indigo-100 text-indigo-700 cursor-pointer hover:bg-indigo-200' :
                        'bg-slate-100 text-slate-400 cursor-not-allowed opacity-60'
                      }`}
                  >
                    {step.id}. {t(step.titleKey).split(' ')[0]}
                  </button>
                )
              })}
            </div>
          </div>
          <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-indigo-600 transition-all duration-500 ease-out"
              style={{ width: `${(currentStep / STEPS_TO_DISPLAY.length) * 100}%` }}
            />
          </div>
        </div>

        {/* 脕REA DE CONTENIDO */}
        <main className="flex-1 flex flex-col relative w-full overflow-x-hidden">
          <div className="flex-1 p-4 md:p-12">
            <div className="max-w-4xl mx-auto w-full h-full flex flex-col">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentStep}
                  variants={stepVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  className="w-full"
                >
                  {renderCurrentStep()}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </main>
      </div>

      {/* WIZARD FOOTER */}
      <footer className="border-t border-slate-200 bg-white py-6 mt-auto">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 flex flex-col md:flex-row items-center justify-between text-xs text-slate-400">
          <p>漏 {new Date().getFullYear()} DatiaFlow. Todos los derechos reservados.</p>
          <div className="flex space-x-4 mt-2 md:mt-0">
            <a href="#" className="hover:text-slate-600 transition-colors">Soporte</a>
            <span className="text-slate-300">|</span>
            <span className="text-slate-500 font-mono">v1.0-beta</span>
          </div>
        </div>
      </footer>

      <Modal
        isOpen={modalState.isOpen}
        title={modalState.title}
        message={modalState.message}
        onClose={closeModal}
        onAccept={modalState.onAccept}
        showCancel={modalState.showCancel}
      />
    </div>
  );
}
